import React, { useState } from 'react';
import { useAuthContext } from '@/context/Auth';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Box, BoxProps, Container, Stack, Text, Title } from '@mantine/core';
import Button from './Button';
import { useResponsive } from '@/hooks/use-responsive';
import { useSectionContent } from '@/hooks/useSectionContent';
import WishlistModal from './WishlistModal';

/**
 * Root
 */
const Root = styled(Box, {
  shouldForwardProp: (prop: PropertyKey) => ![''].includes(prop.toString()),
})<Partial<UnitFactorAssessmentCombinedProps>>((props) => css``);

/**
 * UnitFactorAssessment Component
 */
export interface UnitFactorAssessmentProps {
  [prop: string]: any;
}

export type UnitFactorAssessmentCombinedProps = UnitFactorAssessmentProps & BoxProps;

const UnitFactorAssessment: React.FC<UnitFactorAssessmentCombinedProps> = (props) => {
  const { dispatch } = useAuthContext();
  const { responsive } = useResponsive();
  const { content, error } = useSectionContent("unit-factor-assessment");
  const [modalOpened, setModalOpened] = useState(false);

  if (error) return null;
  if (!content) return null;

  return (
    <>
      <Root
        bg={content.style?.background || "gray.0"}
        py={responsive(content.layout?.padding || {
          xs: 40,
          lg: 64
        })}
        {...props}
      >
        <Container
          size="xl"
          mah={content.style?.container?.maxHeight}
        >
          <Stack
            gap={responsive({
              xs: content.style?.grid?.spacing || 10,
              md: content.style?.grid?.verticalSpacing || 20
            })}
            ta="center"
            align="center"
          >
            <Title
              size={responsive({ xs: 28, md: 36 })}
              fw={700}
            >
              {content.title}
            </Title>

            <Text
              fz={responsive({ xs: 16, md: 20 })}
              fw={300}
              maw={800}
              px={responsive({ xs: 20, md: 0 })}
            >
              {content.description}
            </Text>

            <Stack
              align="center"
              mt={content.style?.featuresGrid?.marginY?.xs || 20}
            >
              {content?.launchDate && (
                <Text
                  fz={responsive({ xs: 12, md: 14 })}
                  fw={500}
                  c="dark.3"
                >
                  Service Launching: {content?.launchDate}
                </Text>
              )}

              {content.buttonText && (
                <>
                  <Button
                    variant="outline"
                    transition={false}
                    hasArrow
                    onClick={() => setModalOpened(true)}
                    component='button'
                    style={{
                      marginTop: 15,
                      width: responsive({ xs: 180, md: 220 }),
                      justifyContent: 'center',
                    }}
                  >
                    {content.buttonText}
                  </Button>
                </>
              )}
            </Stack>
          </Stack>
        </Container>
      </Root>

      <WishlistModal 
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        service={content.title || 'unit-factor-assessment'}
      />
    </>
  );
};

export default UnitFactorAssessment;